/**
 * Snapchat Advertising Integration Module (Stories 19.7 + 19.8)
 *
 * Barrel export for OAuth, REST helpers, errors, analytics/reports/builders/
 * transformers, and the Snapchat Ads adapter.
 *
 * Snapchat Ads characteristics worth knowing about before reading this code:
 *  - OAuth 2.0 + PKCE against the Snap Marketing API v1
 *    (`https://accounts.snapchat.com/login/oauth2/authorize` for authorize,
 *     `https://accounts.snapchat.com/login/oauth2/access_token` for token)
 *  - Form-body credentials on the token endpoint (NOT HTTP Basic Auth like
 *    Pinterest — matches Microsoft Ads / Klaviyo)
 *  - Aggressive 60-minute access TTL — `inlineRefreshOn401: true` is REQUIRED
 *    by the TD4 invariant (access TTL < 24h)
 *  - Refresh tokens treated as effectively permanent (Klaviyo-class) — Snap
 *    docs do NOT specify a refresh-token TTL; observed behaviour shows the
 *    field is always populated on refresh responses, so the adapter
 *    defensively persists any rotated refresh_token returned
 *  - Discovery is a 2-level org→ad-account hierarchy flattened in a single
 *    request via `/v1/me/organizations?with_ad_accounts=true`
 *  - Provider account ID is the lex-asc-min UUID (Snap IDs are UUIDs — no
 *    BigInt-aware sort needed unlike Pinterest)
 *  - **Story 19.8 wires the stats pipeline:** 6 parallel sync stats
 *    pipelines with field batching for Snap's hard 50-field cap, plus an
 *    async stats reports fallback (CSV plain, GET-with-`?async=true`, 24h
 *    URL TTL) for >50-field fetches and creative/media-level breakdowns.
 *
 * The folder is named `snapchat/` (not `snapchat-ads/`) to leave room for
 * future Snap properties (Snap Login Kit, Snap Maps, Public Profile API),
 * mirroring how `pinterest/` and `microsoft/` are structured.
 */

import { registerAdapter } from "../registry";
import { snapchatAdsAdapter } from "./snapchat-ads";

// Register the adapter so getAdapter("snapchat-ads") returns it.
registerAdapter("snapchat-ads", snapchatAdsAdapter);

// Platform values (shared constant for frontend/backend alignment, mirrors MS Ads)
export const SNAPCHAT_ADS_PLATFORM_VALUES = ["snapchat-ads"] as const;
export type SnapchatAdsPlatform =
  (typeof SNAPCHAT_ADS_PLATFORM_VALUES)[number];

// OAuth utilities
export {
  buildSnapchatAdsAuthUrl,
  exchangeSnapchatAdsCode,
  refreshSnapchatAdsToken,
  parseSnapchatAdsCallbackError,
  validateTokenResponse,
  validateScope,
  extractOAuthErrorCode,
  extractOAuthErrorDescription,
  SNAPCHAT_ADS_SCOPES,
  SnapchatAdsTokenError,
  type SnapchatAdsTokenErrorOptions,
  type SnapchatAdsAuthUrlOptions,
  type SnapchatAdsTokenResponse,
  type SnapchatAdsCallbackError,
} from "./oauth";

// REST helpers
export {
  buildSnapchatHeaders,
  fetchSnapchatAdsMe,
  fetchSnapchatAdsOrganizationsWithAdAccounts,
  SNAPCHAT_ADS_API_BASE,
  SNAPCHAT_MAX_ORGS,
  SNAPCHAT_MAX_AD_ACCOUNTS,
  type SnapchatAdsUserInfo,
  type SnapchatAdAccount,
  type SnapchatOrganization,
  type SnapchatOrganizationsResult,
} from "./api";

// Error parsing
export {
  parseSnapchatAdsApiError,
  snapchatAdsErrorTypeToAdapterCode,
  SnapchatAdsApiCallError,
  type SnapchatAdsApiError,
  type SnapchatAdsErrorType,
} from "./errors";

// Analytics — sync stats HTTP + parsing + field batching (Story 19.8)
export {
  SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST,
  SNAPCHAT_ADS_MAX_DAY_GRANULARITY_DAYS,
  SNAPCHAT_ADS_MAX_HOUR_GRANULARITY_DAYS,
  SNAPCHAT_ADS_DELIVERY_ONLY_DIMENSIONS,
  MAX_CAMPAIGNS_PAGES,
  SNAPCHAT_ADS_MAX_PAGE_SIZE,
  SNAPCHAT_SYNC_TIMEOUT_MS,
  buildStatsUrl,
  chunkFieldsForBatching,
  fetchSnapchatAdsAdAccountStats,
  fetchSnapchatAdsCampaignsList,
  fetchSnapchatAdsSyncStats,
  isSnapAdsApiUrl,
  parseSnapchatAdsSyncResponse,
  type SnapchatAdsBreakdown,
  type SnapchatAdsCampaign,
  type SnapchatAdsCampaignsListResult,
  type SnapchatAdsGranularity,
  type SnapchatAdsReportDimension,
  type SnapchatAdsReportType,
  type SnapchatAdsStatsEnvelope,
  type SnapchatAdsStatsOptions,
  type SnapchatAdsStatsRow,
  type SnapchatAttributionWindows,
} from "./analytics";

// Async reports — Create / Poll / Download / CSV (Story 19.8)
export {
  POLL_INTERVAL_MS_INITIAL,
  POLL_INTERVAL_MS_MAX,
  POLL_INTERVAL_MULTIPLIER,
  MAX_POLL_ATTEMPTS_SMALL,
  MAX_POLL_ATTEMPTS_LARGE,
  CREATE_TIMEOUT_MS,
  POLL_TIMEOUT_MS,
  DOWNLOAD_TIMEOUT_MS,
  createSnapchatAdsAsyncStatsReport,
  pollSnapchatAdsAsyncReport,
  pollSnapchatAdsAsyncReportUntilDone,
  downloadSnapchatAdsAsyncReport,
  parseSnapchatAdsCsv,
  type PollUntilDoneOptions,
  type SnapchatAdsAsyncCreateOptions,
  type SnapchatAdsAsyncCreateResponse,
  type SnapchatAdsAsyncEntity,
  type SnapchatAdsAsyncFormat,
  type SnapchatAdsAsyncPollResponse,
  type SnapchatAdsAsyncStatus,
} from "./reports";

// Report builders (URL/body composition, field lists, tz-aware dates) (Story 19.8)
export {
  AD_SQUADS_FIELDS,
  ADS_FIELDS,
  AGE_GENDER_FIELDS,
  CAMPAIGNS_FIELDS,
  COUNTRY_OS_FIELDS,
  FIELDS_BY_REPORT_TYPE,
  GRANULARITY_BY_REPORT_TYPE,
  OVERVIEW_FIELDS,
  ROUTING_BY_REPORT_TYPE,
  buildAsyncStatsRequest,
  buildSyncStatsRequest,
  dateToSnapTimestamp,
  type BuildAsyncStatsRequestInput,
  type BuildAsyncStatsRequestOutput,
  type BuildSyncStatsRequestInput,
  type BuildSyncStatsRequestOutput,
} from "./report-builders";

// Transformers — Snap snake_case → camelCase records (Story 19.8)
export {
  TRANSFORMERS,
  parseDurationMillis,
  parseMetric,
  parseMicroCurrency,
  parseSnapPercent,
  safeRoas,
  transformAdSquadsRows,
  transformAdsRows,
  transformAgeGenderRows,
  transformCampaignsRows,
  transformCountryOsRows,
  transformOverviewRows,
} from "./transformers";

// Adapter
export {
  snapchatAdsAdapter,
  SNAPCHAT_ADS_METRICS,
  SNAPCHAT_ADS_DIMENSIONS,
  SNAPCHAT_ADS_API_VERSION,
  SNAPCHAT_ADS_REPORT_TYPES,
  MAX_FETCHMETRICS_DAYS as SNAPCHAT_ADS_MAX_FETCHMETRICS_DAYS,
  buildSnapchatAdsCacheKey,
} from "./snapchat-ads";
